import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { AuthContext } from "../contexts";
import { axiosInstance } from "../utils";

// Custom header required by the backend as CSRF defence-in-depth on top of
// SameSite=Lax JWT cookies. Browsers won't let cross-origin JS set this
// without a preflight the backend wouldn't approve.
const XHR_HEADERS = { "X-Requested-With": "XMLHttpRequest" };

// Old behavior: setInterval(refreshStatus, 30 * 1000). At ~1.3 req/min that's
// ~240 requests per active workday from this hook alone — a quarter of the
// previous prod throttle budget. The new shape is event-driven:
//   - one fetch on mount (every authenticated session does this),
//   - if the banner can never apply (regular CSM, no active session), stop,
//   - otherwise re-fetch on tab focus / visibility change (debounced) with
//     a fallback poll for tabs left open without focus events — fast while a
//     session is actually live (the banner counts down to a hard expiry),
//     slow when merely eligible.
const FOCUS_REFRESH_DEBOUNCE_MS = 60 * 1000; // at most one refresh per minute on focus
const BACKGROUND_POLL_INTERVAL_MS = 5 * 60 * 1000; // fallback for idle-open eligible tabs
const ACTIVE_SESSION_POLL_INTERVAL_MS = 30 * 1000; // live impersonation: keep the expiry countdown honest

/**
 * Manages the active impersonation session — status polling, start, release,
 * and candidate typeahead. Returns 404s from a flag-off backend as
 * `featureEnabled: false`, which the banner uses to hide entirely.
 */
export const useImpersonation = () => {
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollTimer = useRef(null);
  const lastFetchAtRef = useRef(0);

  const refreshStatus = useCallback(async () => {
    try {
      lastFetchAtRef.current = Date.now();
      const { data } = await axiosInstance.get("/impersonation/status/");
      setStatus(data);
    } catch (err) {
      if (err.response?.status === 401) {
        setStatus(null);
      } else {
        setStatus({
          impersonating: false,
          feature_enabled: false,
          is_superuser_eligible: false,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch — one per authenticated session. The status response is
  // what tells us whether to keep polling (eligible superuser, or currently
  // being impersonated). Skipped for unauthenticated users.
  useEffect(() => {
    if (!user) {
      setStatus(null);
      setLoading(false);
      return;
    }
    refreshStatus();
  }, [user, refreshStatus]);

  // Derived as primitives so the polling effect below depends on these two
  // fields directly — it re-runs only when they flip, not on every status
  // refresh (each poll returns a fresh object). Keeps exhaustive-deps happy
  // without over-triggering the effect.
  const isSuperuserEligible = status?.is_superuser_eligible ?? false;
  const isImpersonating = status?.impersonating ?? false;

  // Event-driven re-fetch — only enabled when the banner could actually
  // matter for this session. Regular CSMs (not superuser-eligible AND not
  // inside an impersonation session) get exactly one /impersonation/status/
  // call per page load and zero polling, which removes ~99% of the load
  // this hook used to produce on the shared per-user throttle bucket.
  useEffect(() => {
    if (!user) return undefined;
    const bannerRelevant = isSuperuserEligible || isImpersonating;
    if (!bannerRelevant) return undefined;

    const maybeRefresh = () => {
      const now = Date.now();
      if (now - lastFetchAtRef.current >= FOCUS_REFRESH_DEBOUNCE_MS) {
        refreshStatus();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") maybeRefresh();
    };

    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", onVisibility);

    // A focused-but-idle tab fires no focus/visibility events, so the interval
    // is the only thing that can catch a server-side change. While a session is
    // live the banner advertises an active impersonation and counts down to a
    // hard expiry — poll fast enough that it can't keep claiming an active
    // session for minutes after it has expired. Merely-eligible tabs (no live
    // session) only need the slow idle-open fallback. The effect re-runs when
    // `isImpersonating` flips (it's in the dep array), so the cadence
    // tracks the session lifecycle.
    const pollIntervalMs = isImpersonating
      ? ACTIVE_SESSION_POLL_INTERVAL_MS
      : BACKGROUND_POLL_INTERVAL_MS;
    pollTimer.current = setInterval(refreshStatus, pollIntervalMs);

    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(pollTimer.current);
    };
  }, [user, isSuperuserEligible, isImpersonating, refreshStatus]);

  const start = useCallback(async ({ targetUserId, password }) => {
    await axiosInstance.post(
      "/impersonation/start/",
      { target_user_id: targetUserId, password },
      { headers: XHR_HEADERS },
    );
    // Reload — the JWT cookie now belongs to the target. Reloading rehydrates
    // every context (auth, integrations, feature flags) from /users/me/ as
    // that user, which is much safer than trying to selectively invalidate.
    window.location.assign("/");
  }, []);

  const release = useCallback(async () => {
    await axiosInstance.post("/impersonation/release/", null, {
      headers: XHR_HEADERS,
    });
    window.location.assign("/");
  }, []);

  const searchCandidates = useCallback(async (query) => {
    const { data } = await axiosInstance.get("/impersonation/candidates/", {
      params: query ? { q: query } : {},
    });
    return data.results || [];
  }, []);

  return {
    status,
    loading,
    refreshStatus,
    start,
    release,
    searchCandidates,
  };
};
